import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { DateTime } from 'luxon';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageModule } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzStatisticModule } from 'ng-zorro-antd/statistic';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NgxEchartsDirective, provideEcharts } from 'ngx-echarts';
import { DateRangeSelectorComponent } from '../../../basic/date-range-selector/date-range-selector.component';
import { TruncatePipe } from '../../../basic/pipes/truncate.pipe';
import { Board } from '../../../core/board/board';
import { BoardHolderService } from '../../../core/board/board.holder.service';
import { BoardService } from '../../../core/board/board.service';
import { CompanyService } from '../../../core/company/company.service';
import { CoreItemService } from '../../../core/core-item/core-item.service';
import { Leads, LeadsStatus, LeadsTimeRangeData, WeeklyLeadsStatus } from '../../../core/leads/leads';
import { LeadsService } from '../../../core/leads/leads.service';
import { ProductService } from '../../../core/product/product.service';
import { ResumeService } from '../../../core/resume/resume.service';
import { LeadsTimeRangeCardComponent } from '../../../domain/leads/leads-time-range-card/leads-time-range-card.component';
import { BasicPageComponent } from '../../_shared/basic-page/basic-page.component';

@Component({
  selector: 'app-leads-reports',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,

    NzInputModule,
    NzIconModule,
    NzButtonModule,
    NzTableModule,
    NzPopoverModule,
    NzModalModule,
    NzAlertModule,
    NzPopconfirmModule,
    NzTabsModule,
    NzMessageModule,
    NzSelectModule,
    NzDropDownModule,
    NzTagModule,

    TruncatePipe,

    MatTooltip,

    BasicPageComponent,
    DateRangeSelectorComponent,
    LeadsTimeRangeCardComponent,

    NgxEchartsDirective,

    NzStatisticModule,
  ],
  templateUrl: './leads-reports.component.html',
  styleUrl: './leads-reports.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideEcharts()],
})
export class LeadsReportsComponent {
  constructor(
    private leadsService: LeadsService,
    private companyService: CompanyService,
    private resumeService: ResumeService,
    private productService: ProductService,
    private coreItemService: CoreItemService,
    private boardService: BoardService,
    private boardHolderService: BoardHolderService
  ) {}

  leadsList = toSignal(this.leadsService.itemList$(), { initialValue: [] });

  companyList = toSignal(this.companyService.itemList$(), { initialValue: [] });
  resumeList = toSignal(this.resumeService.resumeList$(), { initialValue: [] });
  productList = toSignal(this.productService.productList$(), { initialValue: [] });

  ngOnInit(): void {}

  myWeeklyLeads = computed(() => {
    const weeklyLeads = this.weeklyLeads();

    console.log('----- myPersonalBoard:', this.boardHolderService.myPersonalBoard());

    return weeklyLeads.filter((c) => this.boardHolderService.myPersonalBoard()!.id === c.board.id);
  });

  weeklyLeads = computed(() => {
    const leadsList = this.leadsList();

    const boardIdAndLeadsList = new Map<string, Leads[]>();

    leadsList.forEach((leads) => {
      if (!boardIdAndLeadsList.has(leads.createdBy?.id)) {
        boardIdAndLeadsList.set(leads.createdBy?.id, []);
      }

      boardIdAndLeadsList.get(leads.createdBy?.id)!.push(leads);
    });

    let leadsTimeRangeList: LeadsTimeRangeData[] = [];

    boardIdAndLeadsList.forEach((leadsList, boardId) => {
      const list = this.coreItemService.getCoreItemListByIdList([boardId]).filter((c) => !!c);

      if (list.length > 0) {
        const weeklyLeads = this.weeklyLeadsByItemList(leadsList, list[0] as Board);
        leadsTimeRangeList = leadsTimeRangeList.concat(weeklyLeads);
      }
    });

    console.log('----- leadsTimeRangeList:', leadsTimeRangeList);

    return leadsTimeRangeList;
  });

  weeklyLeadsByItemList(leadsList: Leads[], board: Board) {
    const weeklyStartTimeStrAndLeads = new Map<string, Leads[]>();

    leadsList.forEach((leads) => {
      if (leads.createdTime) {
        const weeklyStartTimeStr = leads.createdTime.startOf('week').toISO()!;

        if (!weeklyStartTimeStrAndLeads.has(weeklyStartTimeStr)) {
          weeklyStartTimeStrAndLeads.set(weeklyStartTimeStr, []);
        }

        weeklyStartTimeStrAndLeads.get(weeklyStartTimeStr)!.push(leads);
      }
    });

    const leadsDataList: LeadsTimeRangeData[] = [];

    for (const item of weeklyStartTimeStrAndLeads) {
      const weeklyStartTime = DateTime.fromISO(item[0]);
      const weeklyEndTime = weeklyStartTime.endOf('week');

      const leadsList = item[1];

      const pendingInvites = leadsList.filter(
        (c) =>
          c.status === LeadsStatus.linkedInInviteDraft ||
          c.status === LeadsStatus.linkedInInviteFailed ||
          c.status === LeadsStatus.linkedInInviteProcessing
      );
      const sentInvitations = leadsList.filter((c) => c.status === LeadsStatus.linkedInInvitePending);
      const acceptedInvitations = leadsList.filter(
        (c) =>
          c.status === LeadsStatus.linkedInSecondMessageDraft ||
          c.status === LeadsStatus.linkedInSecondMessageProcessing ||
          c.status === LeadsStatus.linkedInSecondMessageSent ||
          c.status === LeadsStatus.linkedInSecondMessageFailed
      );
      const respondedInvitations = leadsList.filter((c) => c.status === LeadsStatus.linkedInResponded);

      const totalSentInvitationsCount =
        sentInvitations.length + acceptedInvitations.length + respondedInvitations.length;
      const totalAcceptedInvitationsCount = acceptedInvitations.length + respondedInvitations.length;

      //#region Industry
      const industryDataList: {
        name: string;
        value: number;
      }[] = [];

      const resumeList = this.resumeList();
      const companyList = this.companyList();

      const companyCategoryAndLeads = new Map<string, Leads[]>();

      leadsList.forEach((leads) => {
        const resume = resumeList.find((c) => c.id === leads.unsafeSubData.resumeId);

        if (resume?.parentId) {
          const company = companyList.find((c) => c.id === resume.parentId);

          if (company && company.type) {
            if (!companyCategoryAndLeads.has(company.type!)) {
              companyCategoryAndLeads.set(company.type!, []);
            }

            companyCategoryAndLeads.get(company.type!)!.push(leads);
          }
        }
      });

      companyCategoryAndLeads.forEach((value: Leads[], key: string) => {
        industryDataList.push({
          name: key,
          value: value.length,
        });
      });

      if (industryDataList.length === 0) {
        industryDataList.push({
          name: 'No Industry Data',
          value: leadsList.length,
        });
      }
      //#endregion Industry

      //#region Product
      const productDataList: {
        name: string;
        value: number;
      }[] = [];

      const productTitleAndLeads = new Map<string, Leads[]>();

      leadsList.forEach((leads) => {
        const product = this.productList().find((c) => c.id === leads.unsafeSubData.productId);

        if (product) {
          if (productTitleAndLeads.has(product.title ?? '')) {
            productTitleAndLeads.get(product.title ?? '')!.push(leads);
          } else {
            productTitleAndLeads.set(product.title ?? '', [leads]);
          }
        }
      });

      productTitleAndLeads.forEach((value: Leads[], key: string) => {
        productDataList.push({
          name: key,
          value: value.length,
        });
      });

      //#endregion Product

      leadsDataList.push({
        board,
        status: WeeklyLeadsStatus.good,
        startDateTime: weeklyStartTime,
        endDateTime: weeklyEndTime,
        pendingInvitationsCount: pendingInvites.length,
        sentInvitationsCount: totalSentInvitationsCount,
        acceptedInvitationsCount: totalAcceptedInvitationsCount,
        repliedMessageCount: respondedInvitations.length,
        industryPieOption: getIndustryPieOption(industryDataList),
        productPieOption: getProductPieOption(productDataList),
        invitationFunnelOption: getInvitationFunnelOption([
          { value: totalSentInvitationsCount, name: 'Sent Invitations' },
          { value: totalAcceptedInvitationsCount, name: 'Accepted Invitations' },
          { value: respondedInvitations.length, name: 'Responded Invitations' },
        ]),
      });
    }

    leadsDataList.sort((a, b) => b.startDateTime.toMillis() - a.startDateTime.toMillis());

    return leadsDataList;
  }
}

function getProductPieOption(data: { value: number; name: string }[]) {
  return {
    legend: {
      top: 'bottom',
    },
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b} : {c} ({d}%)',
    },
    series: [
      {
        name: 'Product',
        type: 'pie',
        label: {
          show: false,
        },
        radius: [30, 100],
        center: ['50%', '50%'],
        roseType: 'area',
        itemStyle: {
          borderRadius: 8,
        },
        data,
      },
    ],
  };
}

function getIndustryPieOption(data: { value: number; name: string }[]) {
  return {
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b} : {c} ({d}%)',
    },
    series: [
      {
        name: 'Industry',
        type: 'pie',
        label: {
          show: false,
        },
        radius: [30, 100],
        center: ['50%', '50%'],
        roseType: 'area',
        itemStyle: {
          borderRadius: 8,
        },
        data,
      },
    ],
  };
}

function getInvitationFunnelOption(data: { value: number; name: string }[]) {
  const dataLegendList = data.map((c) => c.name);
  return {
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}',
    },
    legend: {
      data: dataLegendList,
      top: 'bottom',
    },
    series: [
      {
        name: 'Funnel',
        type: 'funnel',
        left: '10%',
        top: 60,
        bottom: 60,
        width: '80%',
        minSize: '0%',
        maxSize: '100%',
        sort: 'descending',
        gap: 2,
        label: {
          show: true,
          position: 'inside',
        },
        labelLine: {
          length: 10,
          lineStyle: {
            width: 1,
            type: 'solid',
          },
        },
        itemStyle: {
          borderColor: '#fff',
          borderWidth: 1,
        },
        emphasis: {
          label: {
            fontSize: 20,
          },
        },
        data,
      },
    ],
  };
}
